// src/FNOL-1.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./fonts.css";
import "./FNOLPage.css";
import { fnolAPI, documentsAPI, fraudAPI, claimsAPI, evidenceAPI } from "../../api";
import FNOLSubmissionForm from "../../components/FNOLSubmissionForm";
import sseService from "../../services/sseService";
import fnolStateService from "../../services/fnolStateService";
import NotificationToast from "../../components/NotificationToast";
import FNOLInbox from "../../components/FNOLInbox";
import ProgressStepper from "../../components/ProgressStepper";
import CompanyCard from "../../components/CompanyCard";
import NavBar from "../../components/NavBar";
import DataHandlingPage from "../DataHandling/DataHandlingPage";
import ValidationPage from "../Validation/ValidationPage";
import FraudAnalysisPage from "../FraudAnalysis/FraudAnalysisPage";
import TriagingPage from "../Triaging/TriagingPage";
import ClaimsSetUpPage from "../ClaimsSetUp/ClaimsSetUp";
import { normalizeApiTimeToInput, sanitizeTimeForApi } from "../../utils/time";

/* ---------- Helpers ---------- */
function timeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
}

function convertToIST(timestampStr) {
  // Treat the API timestamp as UTC (ignore the +05:30) and return Date object
  // The formatTime/formatDate functions will handle IST conversion for display
  return new Date(timestampStr.replace(/\+.*$/, 'Z'));
}

function formatCurrencyAmount(value) {
  if (!value) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;

  // Format with commas (e.g. 1,000,000)
  return num.toLocaleString('en-US');
}

const DASHBOARD_INBOX_FILTER_STORAGE_KEY = 'dashboardInboxStatusFilter';
const DASHBOARD_INBOX_FILTERS = {
  NEW: 'new',
  OPEN: 'open',
  PROCESSED: 'processed'
};

const isSupportedDashboardInboxFilter = (value) => {
  return Object.values(DASHBOARD_INBOX_FILTERS).includes(value);
};

const getPersistedDashboardInboxFilter = () => {
  if (typeof window === 'undefined') return '';

  try {
    const persistedFilter = window.sessionStorage.getItem(DASHBOARD_INBOX_FILTER_STORAGE_KEY);
    return isSupportedDashboardInboxFilter(persistedFilter) ? persistedFilter : '';
  } catch (error) {
    return '';
  }
};

const setPersistedDashboardInboxFilter = (statusFilter) => {
  if (typeof window === 'undefined' || !isSupportedDashboardInboxFilter(statusFilter)) return;

  try {
    window.sessionStorage.setItem(DASHBOARD_INBOX_FILTER_STORAGE_KEY, statusFilter);
  } catch (error) {
    // no-op
  }
};

const clearPersistedDashboardInboxFilter = () => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(DASHBOARD_INBOX_FILTER_STORAGE_KEY);
  } catch (error) {
    // no-op
  }
};

const normalizeFnolIdentifier = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const extractClaimsRows = (claimsResponse) => {
  if (Array.isArray(claimsResponse)) return claimsResponse;
  if (Array.isArray(claimsResponse?.data)) return claimsResponse.data;
  if (Array.isArray(claimsResponse?.claims)) return claimsResponse.claims;
  if (Array.isArray(claimsResponse?.results)) return claimsResponse.results;
  return [];
};

const extractProcessedFnolIdsFromClaims = (claimsResponse) => {
  const claimRows = extractClaimsRows(claimsResponse);
  const processedIds = claimRows
    .map((claim) => normalizeFnolIdentifier(
      claim?.fnol_id ?? claim?.fnolId ?? claim?.fnol?.fnol_id ?? claim?.fnol?.id
    ))
    .filter(Boolean);

  return new Set(processedIds);
};

const getInboxItemStatusNumber = (item) => {
  const statusCandidate = item?.status ?? item?.data?.status;
  const numericStatus = Number(statusCandidate);
  return Number.isFinite(numericStatus) ? numericStatus : null;
};

const getInboxItemStatusText = (item) => {
  const statusCandidate = item?.status ?? item?.data?.status;
  if (typeof statusCandidate !== 'string') return '';
  return statusCandidate.trim().toLowerCase();
};

const matchesDashboardInboxStatusFilter = (item, statusFilter, processedFnolIds) => {
  if (!isSupportedDashboardInboxFilter(statusFilter)) return true;

  const numericStatus = getInboxItemStatusNumber(item);
  const textStatus = getInboxItemStatusText(item);
  const itemFnolId = normalizeFnolIdentifier(item?.id ?? item?.data?.fnol_id ?? item?.data?.id);

  if (statusFilter === DASHBOARD_INBOX_FILTERS.NEW) {
    return numericStatus === 0 || textStatus === 'new';
  }

  if (statusFilter === DASHBOARD_INBOX_FILTERS.OPEN) {
    return numericStatus === 1 || textStatus === 'open';
  }

  if (statusFilter === DASHBOARD_INBOX_FILTERS.PROCESSED) {
    return (
      (itemFnolId && processedFnolIds.has(itemFnolId)) ||
      numericStatus === 2 ||
      textStatus === 'processed' ||
      textStatus === 'completed' ||
      textStatus === 'closed'
    );
  }

  return true;
};

/* ---------- Icons (SVG kept inline) ---------- */
const CloseIcon = ({ className = "" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const Req = () => <span className="text-danger ms-1">*</span>;

/* ---------- Main component ---------- */
export default function FNOLDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fnolId, '*': pageType } = useParams();
  const locationPath = location.pathname;
  const requestedInboxStatusFilter = location.state?.inboxStatusFilter;

  // sidebar state (only inbox collapsible)
  const [inboxOpen, setInboxOpen] = useState(true);
  // split screen state
  const [isSplitScreen, setIsSplitScreen] = useState(false);

  // documents state
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [documentSearchQuery, setDocumentSearchQuery] = useState("");
  const selectedDoc = documents.find((d) => d.uniqueKey === selectedDocId);
  const [inlinePreviewUrl, setInlinePreviewUrl] = useState(null);
  const [inlinePreviewLoading, setInlinePreviewLoading] = useState(false);
  // Cache for document preview URLs to prevent redundant fetches
  const [documentPreviewCache, setDocumentPreviewCache] = useState({});

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!documentSearchQuery.trim()) return documents;
    const query = documentSearchQuery.toLowerCase();
    return documents.filter((d) => d.name.toLowerCase().includes(query));
  }, [documents, documentSearchQuery]);

  // inbox mock data (initial)
  const initialInbox = useMemo(() => {
    return [];
  }, []);

  const [inbox, setInbox] = useState(initialInbox);
  // API integration state
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [apiInbox, setApiInbox] = useState([]);
  const [processedFnolIds, setProcessedFnolIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Notification toast state
  const [toastNotification, setToastNotification] = useState(null);
  const [successToast, setSuccessToast] = useState(null);



  // Validity check state
  const [validityData, setValidityData] = useState(null);
  const [loadingValidity, setLoadingValidity] = useState(false);
  const [revalidating, setRevalidating] = useState(false);

  const [showRevalidationModal, setShowRevalidationModal] = useState(false);

  // Fraud analysis state
  const [analysisData, setAnalysisData] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);



  // Evidence state
  const [originalEvidence, setOriginalEvidence] = useState(null);

  // selected item
  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(() => {
    const currentInbox = apiInbox.length > 0 ? apiInbox : inbox;
    return currentInbox.find((i) => i.id === selectedId) || null;
  }, [apiInbox, inbox, selectedId]);

  // Track which FNOL we've already navigated to (to prevent re-navigation on every pathname change)
  const navigatedFnolRef = useRef(null);

  // Complete button state (Managed per FNOL ID)
  const [isCompleted, setIsCompleted] = useState(false);
  const [isValidationCompleted, setIsValidationCompleted] = useState(false);
  const [isFraudAnalysisCompleted, setIsFraudAnalysisCompleted] = useState(false);
  const [isTriagingCompleted, setIsTriagingCompleted] = useState(false);
  const [isClaimsSetUpCompleted, setIsClaimsSetUpCompleted] = useState(false);

  // Claim ID state - when set, all fields become non-editable
  const [claimId, setClaimId] = useState(null);

  // Fetch claim ID from API
  const fetchClaimId = useCallback(async (fnolId) => {
    if (!fnolId) return;
    try {
      const response = await claimsAPI.getClaim(fnolId);
      if (response.success && response.data?.claim_id) {
        setClaimId(response.data.claim_id);

        // Mark all pages as completed when claim ID exists
        try {
          await fnolStateService.markAllAsCompleted(fnolId);

          // Update local state to reflect completion
          setIsCompleted(true);
          setIsValidationCompleted(true);
          setIsFraudAnalysisCompleted(true);
          setIsTriagingCompleted(true);
          setIsClaimsSetUpCompleted(true);


          console.log('✅ All pages marked as completed for claim:', response.data.claim_id);
        } catch (stateError) {
          console.error('Failed to mark pages as completed:', stateError);
        }
      } else {
        setClaimId(null);
      }
    } catch (error) {
      console.error('Error fetching claim:', error);
      setClaimId(null);
    }
  }, []);

  const handleClaimGenerated = useCallback((generatedClaimId) => {
    // Only set claim ID if it's a valid non-empty string
    if (generatedClaimId && generatedClaimId.trim() !== '') {
      setClaimId(generatedClaimId);

      // Mark all pages as completed when claim is generated
      if (selectedId) {
        fnolStateService.markAllAsCompleted(selectedId).then(() => {
          // Update local state to reflect completion
          setIsCompleted(true);
          setIsValidationCompleted(true);
          setIsFraudAnalysisCompleted(true);
          setIsTriagingCompleted(true);
          setIsClaimsSetUpCompleted(true);


          console.log('✅ All pages marked as completed for newly generated claim:', generatedClaimId);
        }).catch(error => {
          console.error('Failed to mark pages as completed:', error);
        });
      }
    } else {
      setClaimId(null);
    }
  }, [selectedId]);

  // Load FNOL state from API when selectedId changes
  const loadFNOLState = useCallback(async (fnolId) => {
    if (!fnolId) return null;

    try {
      const state = await fnolStateService.getState(fnolId);

      // Update local state based on API response
      setIsCompleted(state.DataHandling === 'completed');
      setIsValidationCompleted(state.Validation === 'completed');
      setIsFraudAnalysisCompleted(state.FraudAnalysis === 'completed');
      setIsTriagingCompleted(state.Triaging === 'completed');
      setIsClaimsSetUpCompleted(state.ClaimsSetup === 'completed');


      console.log('✅ FNOL state loaded from API:', state);
      return state; // Return state for navigation logic
    } catch (error) {
      console.error('Failed to load FNOL state from API:', error);
      setIsCompleted(false);
      setIsValidationCompleted(false);
      setIsFraudAnalysisCompleted(false);
      setIsTriagingCompleted(false);
      setIsClaimsSetUpCompleted(false);
      return null; // Return null on error
    }
  }, []);

  useEffect(() => {
    // Immediately clear claim ID when switching FNOLs to prevent showing old claim
    setClaimId(null);

    if (selectedId) {
      // Only navigate to last visited page if this is a NEW FNOL selection
      // (not when user manually navigates within the same FNOL)
      if (navigatedFnolRef.current !== selectedId) {
        navigatedFnolRef.current = selectedId;

        // Load state from API and navigate to last visited page
        loadFNOLState(selectedId).then(state => {
          if (state) {
            // Find the last visited or completed screen
            const screenOrder = ['ClaimsSetup', 'Triaging', 'FraudAnalysis', 'Validation', 'DataHandling'];
            let lastVisitedScreen = 'DataHandling'; // Default

            for (const screen of screenOrder) {
              if (state[screen] === 'visited' || state[screen] === 'completed') {
                lastVisitedScreen = screen;
                break;
              }
            }

            // Navigate to the last visited screen
            const pathMap = {
              'DataHandling': 'datahandling',
              'Validation': 'validation',
              'FraudAnalysis': 'fraud-analysis',
              'Triaging': 'triaging',
              'ClaimsSetup': 'claims-setup'
            };

            const targetPath = `/${selectedId}/${pathMap[lastVisitedScreen]}`;

            // Only navigate if not already on a path for this FNOL
            const currentPathFnolId = location.pathname.split('/')[1];
            if (currentPathFnolId !== String(selectedId)) {
              navigate(targetPath, { replace: true });
              console.log(`🔄 Navigated to last visited screen: ${lastVisitedScreen}`);
            }
          }
        });
      }

      // Fetch claim ID from API (async - will update state when complete)
      fetchClaimId(selectedId);
    } else {
      setIsCompleted(false);
      setIsValidationCompleted(false);
      setIsFraudAnalysisCompleted(false);
      setIsTriagingCompleted(false);
      setIsClaimsSetUpCompleted(false);
    }
    // Reset navigation state when FNOL changes
    setMaxReachedStep(0);
  }, [selectedId, fetchClaimId, loadFNOLState, navigate, location.pathname]);

  const handleComplete = async () => {
    if (!selectedId) return;
    // Prevent status changes if claim ID exists
    if (claimId) {
      console.log('⚠️ Cannot change completion status - claim already exists');
      return;
    }
    setIsSplitScreen(false);

    const newState = !isCompleted;
    setIsCompleted(newState);

    // Update API state
    try {
      await fnolStateService.updateState(selectedId, {
        DataHandling: newState ? 'completed' : 'visited'
      });
      // Update visited states (completed screens are also visited)
      setVisitedStates(prev => ({ ...prev, DataHandling: true }));
      console.log('✅ Data Handling completion state updated in API');
    } catch (error) {
      console.error('Failed to update Data Handling state in API:', error);
    }


    // Navigate back to datahandling if on a different page
    if (currentStep !== 'DataHandling') {
      navigate(`/${selectedId}/datahandling`);
    }
  };

  // Wrapper setters that also update visited states
  const handleSetValidationCompleted = useCallback((newState) => {
    setIsValidationCompleted(newState);
    if (newState) {
      setVisitedStates(prev => ({ ...prev, Validation: true }));
    }
  }, []);

  const handleSetFraudAnalysisCompleted = useCallback((newState) => {
    setIsFraudAnalysisCompleted(newState);
    if (newState) {
      setVisitedStates(prev => ({ ...prev, FraudAnalysis: true }));
    }
  }, []);

  const handleSetTriagingCompleted = useCallback((newState) => {
    setIsTriagingCompleted(newState);
    if (newState) {
      setVisitedStates(prev => ({ ...prev, Triaging: true }));
    }
  }, []);

  const handleSetClaimsSetUpCompleted = useCallback((newState) => {
    setIsClaimsSetUpCompleted(newState);
    if (newState) {
      setVisitedStates(prev => ({ ...prev, ClaimsSetup: true }));
    }
  }, []);

  // Sync URL ID to local state
  useEffect(() => {
    if (fnolId) {
      // Assuming IDs are numeric, but handling strings gracefully
      const id = /^\d+$/.test(fnolId) ? parseInt(fnolId, 10) : fnolId;
      setSelectedId(id);
    }
  }, [fnolId]);

  // Page Navigation State - derived from URL
  const getCurrentStep = () => {
    if (locationPath.includes('/validation')) return 'Validation';
    if (locationPath.includes('/fraud-analysis')) return 'FraudAnalysis';
    if (locationPath.includes('/triaging')) return 'Triaging';
    if (locationPath.includes('/claims-setup')) return 'ClaimsSetup';
    return 'DataHandling';
  };

  const currentStep = getCurrentStep();

  const STEP_ORDER = ['DataHandling', 'Validation', 'FraudAnalysis', 'Triaging', 'ClaimsSetup'];
  const [maxReachedStep, setMaxReachedStep] = useState(0);

  // State to track visited (not just completed) screens
  const [visitedStates, setVisitedStates] = useState({
    DataHandling: false,
    Validation: false,
    FraudAnalysis: false,
    Triaging: false,
    ClaimsSetup: false
  });

  // Update visited states when loading from API
  useEffect(() => {
    if (selectedId) {
      fnolStateService.getState(selectedId).then(state => {
        if (state) {
          setVisitedStates({
            DataHandling: state.DataHandling === 'visited' || state.DataHandling === 'completed',
            Validation: state.Validation === 'visited' || state.Validation === 'completed',
            FraudAnalysis: state.FraudAnalysis === 'visited' || state.FraudAnalysis === 'completed',
            Triaging: state.Triaging === 'visited' || state.Triaging === 'completed',
            ClaimsSetup: state.ClaimsSetup === 'visited' || state.ClaimsSetup === 'completed'
          });
        }
      }).catch(err => {
        console.error('Failed to update visited states:', err);
      });
    }
  }, [selectedId, isCompleted, isValidationCompleted, isFraudAnalysisCompleted, isTriagingCompleted, isClaimsSetUpCompleted]);

  useEffect(() => {
    const currentStepIndex = STEP_ORDER.indexOf(currentStep);

    // Determine max step based on both completed and visited states
    // If a step is completed, the user can go to the *next* step
    // If a step is visited (but not completed), user can access that step
    let flagsMax = 0;

    // Check completed states for unlocking next steps
    if (isClaimsSetUpCompleted) flagsMax = 4;
    else if (isTriagingCompleted) flagsMax = 4;
    else if (isFraudAnalysisCompleted) flagsMax = 3;
    else if (isValidationCompleted) flagsMax = 2;
    else if (isCompleted) flagsMax = 1;

    // Also check visited states - user can access any visited screen
    const visitedIndexes = STEP_ORDER.map((step, index) =>
      visitedStates[step] ? index : -1
    ).filter(idx => idx !== -1);

    if (visitedIndexes.length > 0) {
      const maxVisitedIndex = Math.max(...visitedIndexes);
      flagsMax = Math.max(flagsMax, maxVisitedIndex);
    }

    if (currentStepIndex > -1) {
      setMaxReachedStep(prev => Math.max(prev, currentStepIndex, flagsMax));
    }
  }, [currentStep, isCompleted, isValidationCompleted, isFraudAnalysisCompleted, isTriagingCompleted, isClaimsSetUpCompleted, visitedStates]);

  const goToValidation = async () => {
    if (!selectedId) return;

    // Check if required FNOL fields are filled
    if (!validateRequiredFields(form)) {
      console.warn('⚠️ Cannot navigate to Validation: Required FNOL fields are missing');
      alert('Please complete all required fields in Data Handling before proceeding to Validation.');
      return;
    }

    // Mark as visited in API only if no claim ID exists
    if (!claimId) {
      if (!isValidationCompleted) {
        try {
          const updatedState = await fnolStateService.markAsVisited(selectedId, 'Validation');
          // Update visited states
          setVisitedStates(prev => ({ ...prev, Validation: true }));
          console.log('✅ Validation marked as visited in API');
        } catch (error) {
          console.error('Failed to mark Validation as visited:', error);
        }
      } else {
        setVisitedStates(prev => ({ ...prev, Validation: true }));
      }
    }

    try {
      // Load validity data before navigating
      await loadValidityData(selectedId);
      navigate(`/${selectedId}/validation`);
    } catch (error) {
      console.error('Failed to load validation data:', error);
      // Still navigate to validation page even if loading fails
      navigate(`/${selectedId}/validation`);
    }
  };

  const goToFraudAnalysis = async () => {
    if (!selectedId) return;

    // Check if required FNOL fields are filled
    if (!validateRequiredFields(form)) {
      console.warn('⚠️ Cannot navigate to Fraud Analysis: Required FNOL fields are missing');
      alert('Please complete all required fields in Data Handling before proceeding to Fraud Analysis.');
      return;
    }

    // Mark as visited in API only if no claim ID exists
    if (!claimId) {
      if (!isFraudAnalysisCompleted) {
        try {
          const updatedState = await fnolStateService.markAsVisited(selectedId, 'FraudAnalysis');
          // Update visited states
          setVisitedStates(prev => ({ ...prev, FraudAnalysis: true }));
          console.log('✅ Fraud Analysis marked as visited in API');
        } catch (error) {
          console.error('Failed to mark Fraud Analysis as visited:', error);
        }
      } else {
        setVisitedStates(prev => ({ ...prev, FraudAnalysis: true }));
      }
    }

    try {
      await loadFraudAnalysisData(selectedId);
      navigate(`/${selectedId}/fraud-analysis`);
    } catch (error) {
      console.error('Failed to load fraud analysis data:', error);
      navigate(`/${selectedId}/fraud-analysis`);
    }
  };

  const goToTriaging = async () => {
    if (!selectedId) return;

    // Check if required FNOL fields are filled
    if (!validateRequiredFields(form)) {
      console.warn('⚠️ Cannot navigate to Triaging: Required FNOL fields are missing');
      alert('Please complete all required fields in Data Handling before proceeding to Triaging.');
      return;
    }

    // Mark as visited in API only if no claim ID exists
    if (!claimId) {
      if (!isTriagingCompleted) {
        try {
          const updatedState = await fnolStateService.markAsVisited(selectedId, 'Triaging');
          // Update visited states
          setVisitedStates(prev => ({ ...prev, Triaging: true }));
          console.log('✅ Triaging marked as visited in API');
        } catch (error) {
          console.error('Failed to mark Triaging as visited:', error);
        }
      } else {
        setVisitedStates(prev => ({ ...prev, Triaging: true }));
      }
    }

    navigate(`/${selectedId}/triaging`);
  };

  const goToClaimsSetup = async () => {
    if (!selectedId) return;

    // Check if required FNOL fields are filled
    if (!validateRequiredFields(form)) {
      console.warn('⚠️ Cannot navigate to Claims Setup: Required FNOL fields are missing');
      alert('Please complete all required fields in Data Handling before proceeding to Claims Setup.');
      return;
    }

    // Mark as visited in API only if no claim ID exists
    if (!claimId) {
      if (!isClaimsSetUpCompleted) {
        try {
          const updatedState = await fnolStateService.markAsVisited(selectedId, 'ClaimsSetup');
          // Update visited states
          setVisitedStates(prev => ({ ...prev, ClaimsSetup: true }));
          console.log('✅ Claims Setup marked as visited in API');
        } catch (error) {
          console.error('Failed to mark Claims Setup as visited:', error);
        }
      } else {
        setVisitedStates(prev => ({ ...prev, ClaimsSetup: true }));
      }
    }

    navigate(`/${selectedId}/claims-setup`);
  };

  const goToDataHandling = async () => {
    if (!selectedId) return;

    // Mark as visited in API only if no claim ID exists
    if (!claimId) {
      if (!isCompleted) {
        try {
          const updatedState = await fnolStateService.markAsVisited(selectedId, 'DataHandling');
          // Update visited states
          setVisitedStates(prev => ({ ...prev, DataHandling: true }));
          console.log('✅ Data Handling marked as visited in API');
        } catch (error) {
          console.error('Failed to mark Data Handling as visited:', error);
        }
      } else {
        setVisitedStates(prev => ({ ...prev, DataHandling: true }));
      }
    }

    navigate(`/${selectedId}/datahandling`);
  };


  // API functions
  const loadFNOLsFromAPI = useCallback(async () => {
    try {
      setLoading(true);
      setApiError(null);
      const [response, claimsResponse] = await Promise.all([
        fnolAPI.getFNOLs(),
        claimsAPI.getAllClaims().catch((error) => {
          console.error('Failed to load claims for processed filter:', error);
          return null;
        })
      ]);

      setProcessedFnolIds(extractProcessedFnolIdsFromClaims(claimsResponse));

      if (response.success) {
        console.log('Raw API Response:', response.data);

        // Convert API data to inbox format (loss_name should be in the response)
        const apiItems = response.data.map(fnol => {
          console.log('Processing FNOL:', fnol.fnol_id, 'loss_name:', fnol.loss_name);
          return {
            id: fnol.fnol_id,
            title: `FNOL ${fnol.fnol_id}`,
            company: fnol.insured_name || fnol.claimant || "Unknown Insured",
            lossName: fnol.loss_name || "",
            datetime: convertToIST(fnol.created_at),
            read: fnol.status === 1,
            status: fnol.status,
            brief: fnol.loss_description || "",
            data: fnol
          };
        });
        console.log('Processed API Items with loss names:', apiItems);
        setApiInbox(apiItems);

        // Auto-selection removed to show empty state initially
        // if (apiItems.length > 0 && !selectedId) {
        //   setSelectedId(apiItems[0].id);
        // }
      }
    } catch (error) {
      console.error('Failed to load FNOLs:', error);
      setApiError('Failed to load FNOLs from server');
    } finally {
      setLoading(false);
    }
  }, [selectedId]); // Add selectedId as dependency

  const handleFNOLSubmission = async (submittedFNOL) => {
    setShowSubmissionForm(false);
    // Reload FNOLs to include the new one
    await loadFNOLsFromAPI();
    // Select the newly submitted FNOL
    setTimeout(() => {
      setSelectedId(submittedFNOL.fnol_id);
    }, 100);
  };

  // Load data on component mount
  useEffect(() => {
    loadFNOLsFromAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSE Notification Listener - Auto-refresh inbox on new FNOL
  useEffect(() => {
    console.log('📡 FNOL Dashboard: Setting up SSE notification listener');

    // Subscribe to new FNOL notifications
    const unsubscribeNewFNOL = sseService.on('newFNOL', async (fnolData) => {
      console.log('🆕 FNOL Dashboard: New FNOL notification received!', fnolData);

      // Show toast notification
      setToastNotification(fnolData);

      // Refresh the inbox to include the new FNOL
      // This will NOT change the currently selected FNOL (like WhatsApp)
      try {
        await loadFNOLsFromAPI();
        console.log('✅ Inbox refreshed successfully');

        // Optional: Show a toast notification
        // You can add a toast library like react-toastify for this
        console.log('🔔 New FNOL added to inbox:', fnolData.fnol_id);
      } catch (error) {
        console.error('❌ Failed to refresh inbox:', error);
      }
    });

    // Subscribe to connection events for debugging
    const unsubscribeConnected = sseService.on('connected', () => {
      console.log('✅ SSE: Connected to notification service');
    });

    const unsubscribeDisconnected = sseService.on('disconnected', (data) => {
      console.log('⚠️ SSE: Disconnected from notification service', data);
    });

    const unsubscribeError = sseService.on('error', (error) => {
      console.error('❌ SSE: Error in notification service', error);
    });

    // Cleanup subscriptions on unmount
    return () => {
      console.log('🧹 FNOL Dashboard: Cleaning up SSE listeners');
      unsubscribeNewFNOL();
      unsubscribeConnected();
      unsubscribeDisconnected();
      unsubscribeError();
    };
  }, [loadFNOLsFromAPI]); // Include loadFNOLsFromAPI as dependency

  // mark selected read on change - removed duplicate API call
  // FNOLs are now marked as read via onClick handler in FNOLInbox

  // Auto-dismiss success toast
  useEffect(() => {
    if (successToast) {
      const timer = setTimeout(() => {
        setSuccessToast(null);
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [successToast]);

  const unreadCount = useMemo(() => {
    const currentInbox = apiInbox.length > 0 ? apiInbox : inbox;
    return currentInbox.filter((i) => !i.read).length;
  }, [apiInbox, inbox]);

  // inbox filters: query + date/time ranges
  const [inboxStatusFilter, setInboxStatusFilter] = useState(() => getPersistedDashboardInboxFilter());
  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  useEffect(() => {
    if (!isSupportedDashboardInboxFilter(requestedInboxStatusFilter)) return;

    setInboxStatusFilter(requestedInboxStatusFilter);
    setPersistedDashboardInboxFilter(requestedInboxStatusFilter);
  }, [requestedInboxStatusFilter]);

  // Use API data if available, otherwise fall back to localStorage
  const currentInbox = apiInbox.length > 0 ? apiInbox : inbox;

  const filteredInbox = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sDate = startDate ? new Date(startDate + "T00:00:00") : null;
    const eDate = endDate ? new Date(endDate + "T23:59:59") : null;
    const sTime = startTime ? timeToMinutes(startTime) : null;
    const eTime = endTime ? timeToMinutes(endTime) : null;

    return currentInbox.filter((it) => {
      if (inboxStatusFilter && !matchesDashboardInboxStatusFilter(it, inboxStatusFilter, processedFnolIds)) {
        return false;
      }

      if (q) {
        const hay = (it.company + " " + it.title + " " + (it.data?.fnol_id || "")).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (sDate && it.datetime < sDate) return false;
      if (eDate && it.datetime > eDate) return false;
      if (sTime !== null || eTime !== null) {
        const tm = it.datetime.getHours() * 60 + it.datetime.getMinutes();
        if (sTime !== null && tm < sTime) return false;
        if (eTime !== null && tm > eTime) return false;
      }
      return true;
    });
  }, [currentInbox, inboxStatusFilter, processedFnolIds, query, startDate, endDate, startTime, endTime]);

  function resetFilters() {
    setInboxStatusFilter("");
    clearPersistedDashboardInboxFilter();
    setQuery("");
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
  }

  async function toggleRead(id) {
    if (apiInbox.length > 0) {
      const item = apiInbox.find((it) => it.id === id);
      if (!item) return;

      const newStatus = item.read ? 0 : 1; // Toggle status

      try {
        // Update status on the server first
        await fnolAPI.updateFNOLStatus(id, newStatus);
        // Only update local state after successful API call
        setApiInbox((prev) => prev.map((it) => (it.id === id ? { ...it, read: !it.read } : it)));
      } catch (error) {
        console.error('Failed to update FNOL status:', error);
        // Don't update local state if API call fails
        // Optionally show user feedback here
      }
    } else {
      // Update local inbox (no API for local storage)
      setInbox((prev) => prev.map((it) => (it.id === id ? { ...it, read: !it.read } : it)));
    }
  }

  // preview modal
  const [previewDoc, setPreviewDoc] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // document preview state
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  function openPreview(doc) {
    setPreviewDoc(doc);
    setIsPreviewOpen(true);
    loadDocumentPreview(doc);
  }

  function closePreview() {
    setIsPreviewOpen(false);
    setTimeout(() => {
      setPreviewDoc(null);
      setPreviewContent(null);
      setPreviewError(null);
    }, 200);
  }

  // Load document preview with proper headers to bypass ngrok warning
  const loadDocumentPreview = async (doc) => {
    if (!doc) return;

    // Check cache first
    if (documentPreviewCache[doc.uniqueKey]) {
      console.log('Using cached modal preview for:', doc.uniqueKey);
      setPreviewContent(documentPreviewCache[doc.uniqueKey]);
      setPreviewLoading(false);
      return;
    }

    try {
      setPreviewLoading(true);
      setPreviewError(null);

      const response = await fetch(doc.preview_url, {
        headers: {
          'ngrok-skip-browser-warning': 'true',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load document: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      // Cache the preview URL
      setDocumentPreviewCache(prev => ({
        ...prev,
        [doc.uniqueKey]: objectUrl
      }));

      setPreviewContent(objectUrl);
    } catch (error) {
      console.error('Failed to load document preview:', error);
      setPreviewError('Failed to load document preview');
    } finally {
      setPreviewLoading(false);
    }
  };
  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      // Cleanup modal preview
      if (previewContent && previewContent.startsWith('blob:')) {
        URL.revokeObjectURL(previewContent);
      }
      // Cleanup all cached previews
      Object.values(documentPreviewCache).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previewContent, documentPreviewCache]);

  // center form state
  const [form, setForm] = useState({
    lossName: "",
    claimant: "",
    dateOfLoss: "",
    timeOfLoss: "",
    cause: "",
    description: "",
    currency: "",
    amount: "",
    location: "",
    gps: "",
    reportedDate: "",
    reportedBy: "",
    globalLossEvent: "",
    policyNumber: "",
    insuredName: "",
    inceptionDate: "",
    expirationDate: "",
    policyStatus: "Active",
    lineOfBusiness: "Property",
    broker: "",
    subLimitType: {},
  });

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalForm, setOriginalForm] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loadingFNOL, setLoadingFNOL] = useState(false);

  function setF(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
    // Check if there are changes
    if (originalForm && originalForm[k] !== v) {
      setHasChanges(true);
    }
  }

  // Validate if all required FNOL fields are filled
  const validateRequiredFields = useCallback((formData) => {
    return !!(
      formData?.lossName &&
      formData?.dateOfLoss &&
      formData?.timeOfLoss &&
      formData?.cause &&
      formData?.description &&
      formData?.currency &&
      formData?.amount &&
      formData?.location &&
      formData?.reportedDate &&
      formData?.policyNumber &&
      formData?.insuredName &&
      formData?.inceptionDate &&
      formData?.expirationDate &&
      formData?.policyStatus &&
      formData?.lineOfBusiness
    );
  }, []);

  // Route protection: Redirect to datahandling if required fields are missing
  useEffect(() => {
    // Only apply protection if we have a selected FNOL and we're not on the datahandling page
    if (!selectedId || currentStep === 'DataHandling') return;

    // Protected routes that require form validation
    const protectedRoutes = ['Validation', 'FraudAnalysis', 'Triaging', 'ClaimsSetup'];

    if (protectedRoutes.includes(currentStep)) {
      // Wait until the selected FNOL record is resolved and form hydration is complete.
      if (!selected || loadingFNOL) return;

      // If FNOL data has not hydrated yet, do not redirect prematurely.
      const isFormLoaded = form && (form.lossName || form.policyNumber);
      if (!isFormLoaded) return;

      // Check if required fields are filled
      const isValid = validateRequiredFields(form);

      if (!isValid) {
        console.warn(`⚠️ Required FNOL fields are missing. Redirecting to Data Handling page.`);
        // Redirect to datahandling page
        navigate(`/${selectedId}/datahandling`, { replace: true });
      }
    }
  }, [selectedId, selected, currentStep, form, loadingFNOL, validateRequiredFields, navigate]);

  // Load FNOL data when item is selected
  const loadFNOLData = async (fnolId) => {
    if (!fnolId) return;

    try {
      setLoadingFNOL(true);
      const response = await fnolAPI.getFNOL(fnolId);
      if (response.success) {
        const fnolData = response.data;

        // Helper to map API data to form state
        const mapFNOLDataToForm = (fnolData, policyData = {}) => {
          return {
            lossName: fnolData.loss_name || "",
            claimant: fnolData.claimant || "",
            dateOfLoss: fnolData.date_of_loss ? fnolData.date_of_loss.split('T')[0] : "",
            timeOfLoss: normalizeApiTimeToInput(fnolData.time_of_loss),
            cause: fnolData.cause_of_loss || "",
            description: fnolData.loss_description || "",
            currency: fnolData.estimated_loss_currency || "",
            amount: fnolData.estimated_loss_amount?.toString() || "",
            location: fnolData.loss_location || "",
            gps: fnolData.gps_coordinates || "",
            reportedDate: fnolData.report_date ? fnolData.report_date.split('T')[0] : "",
            reportedBy: fnolData.reported_by || "",
            globalLossEvent: fnolData.global_loss_details || "",
            policyNumber: fnolData.policy_number || "",
            multiplePolicyNumber: fnolData.multiple_policy_number || null,
            // Policy details
            insuredName: policyData.insured_name || "",
            inceptionDate: policyData.inception_date ? policyData.inception_date.split('T')[0] : "",
            expirationDate: policyData.expiration_date ? policyData.expiration_date.split('T')[0] : "",
            policyStatus: policyData.policy_status || "",
            lineOfBusiness: policyData.line_of_business || "",
            broker: policyData.broker_name || "",
            subLimitType: fnolData.sublimit && typeof fnolData.sublimit === 'object'
              ? fnolData.sublimit
              : (typeof fnolData.sublimit === 'string'
                ? fnolData.sublimit.split(',').reduce((acc, s) => {
                  if (s.trim()) acc[s.trim()] = true;
                  return acc;
                }, {})
                : {}),
          };
        };

        // Initialize form data with FNOL data
        let policyData = {};

        // Fetch policy details if policy number exists
        if (fnolData.policy_number) {
          try {
            const policyResponse = await fnolAPI.getPolicyDetails(fnolData.policy_number);
            if (policyResponse.success) {
              policyData = policyResponse.data;
            }
          } catch (policyError) {
            console.error('Failed to load policy details:', policyError);
            // Continue with empty policy details
          }
        }

        const formData = mapFNOLDataToForm(fnolData, policyData);

        setForm(formData);
        setOriginalForm(formData);
        setHasChanges(false);
        setIsEditMode(false);
      }
    } catch (error) {
      console.error('Failed to load FNOL data:', error);
    } finally {
      setLoadingFNOL(false);
    }
  };

  // Load documents for FNOL
  const loadDocuments = useCallback(async (fnolId) => {
    if (!fnolId) return;

    try {
      setLoadingDocuments(true);
      const response = await documentsAPI.getDocuments(fnolId);
      if (response.attachments) {
        console.log('Documents loaded from API:', response.attachments);

        // Transform the attachments data to match expected format
        const documentsWithUniqueKeys = response.attachments.map((attachment, index) => ({
          id: attachment.metadata.id,
          name: attachment.metadata.document_name,
          preview_url: attachment.preview_url.replace('http://', 'https://'), // Convert HTTP to HTTPS for CORS
          download_url: attachment.download_url.replace('http://', 'https://'), // Convert HTTP to HTTPS for CORS
          uploaded_at: attachment.metadata.created_at,
          category: attachment.metadata.document_category,
          description: attachment.metadata.document_description,
          ai_suggested_name: attachment.metadata.ai_suggested_document_name,
          document_id: attachment.metadata.document_id,
          uniqueKey: `${attachment.metadata.id}-${index}`,
          originalId: attachment.metadata.id
        }));

        console.log('Documents with unique keys:', documentsWithUniqueKeys);
        setDocuments(documentsWithUniqueKeys);

        // Set default document: prioritize email_body.pdf, otherwise use first document
        // Check if current selection exists in the new documents list
        if (documentsWithUniqueKeys.length > 0) {
          const currentDocExists = selectedDocId && documentsWithUniqueKeys.some(doc => doc.uniqueKey === selectedDocId);

          if (!currentDocExists) {
            // Current selection doesn't exist in new list, set default
            const emailBodyDoc = documentsWithUniqueKeys.find(doc =>
              doc.name && doc.name.toLowerCase().includes('email_body.pdf')
            );
            const defaultDoc = emailBodyDoc || documentsWithUniqueKeys[0];
            setSelectedDocId(defaultDoc.uniqueKey);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [selectedDocId]);

  // Load validity check data for FNOL
  const loadValidityData = useCallback(async (fnolId) => {
    if (!fnolId) return;

    try {
      setLoadingValidity(true);
      const response = await fnolAPI.getValidityCheck(fnolId);
      if (response.success) {
        setValidityData(response.data);
      } else if (response.notFound) {
        // Try to create validity check
        try {
          const createResponse = await fnolAPI.createValidityCheck(fnolId);
          setValidityData(createResponse);
        } catch (createError) {
          console.error('Failed to create validity check:', createError);
          setValidityData(null);
        }
      }
    } catch (error) {
      console.error('Failed to load validity data:', error);
      setValidityData(null);
    } finally {
      setLoadingValidity(false);
    }
  }, []);

  // Load fraud analysis data for FNOL
  const loadFraudAnalysisData = useCallback(async (fnolId) => {
    if (!fnolId) return;

    try {
      setLoadingAnalysis(true);
      const response = await fraudAPI.getFraudAnalysis(fnolId);
      if (response.success) {
        setAnalysisData(response.data);
      } else if (response.notFound) {
        try {
          const createResponse = await fraudAPI.createFraudAnalysis(fnolId);
          setAnalysisData(createResponse);
        } catch (createError) {
          console.error('Failed to create fraud analysis:', createError);
          setAnalysisData(null);
        }
      }
    } catch (error) {
      console.error('Failed to load fraud analysis data:', error);
      setAnalysisData(null);
    } finally {
      setLoadingAnalysis(false);
    }
  }, []);

  // Load original evidence for FNOL
  const loadOriginalEvidence = useCallback(async (fnolId) => {
    if (!fnolId) return;
    try {
      const response = await evidenceAPI.getOriginalEvidence(fnolId);
      setOriginalEvidence(response);
    } catch (error) {
      console.error('Failed to load original evidence:', error);
      setOriginalEvidence(null);
    }
  }, []);

  // Load inline preview for selected document
  useEffect(() => {
    const loadInlinePreview = async () => {
      // Find the current selected document
      const currentDoc = documents.find((d) => d.uniqueKey === selectedDocId);

      console.log('Loading inline preview - selectedDocId:', selectedDocId);
      console.log('Current document:', currentDoc);

      if (!currentDoc?.preview_url) {
        console.log('No preview URL available');
        setInlinePreviewUrl(null);
        setInlinePreviewLoading(false);
        return;
      }

      // Check cache first
      if (documentPreviewCache[currentDoc.uniqueKey]) {
        console.log('Using cached preview for:', currentDoc.uniqueKey);
        setInlinePreviewUrl(documentPreviewCache[currentDoc.uniqueKey]);
        setInlinePreviewLoading(false);
        return;
      }

      // Check if file type is supported for inline preview
      const fileExtension = currentDoc.name.split('.').pop().toLowerCase();
      const supportedTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'];

      if (!supportedTypes.includes(fileExtension)) {
        console.log('File type not supported for inline preview:', fileExtension);
        setInlinePreviewUrl(null);
        setInlinePreviewLoading(false);
        return;
      }

      try {
        setInlinePreviewLoading(true);
        console.log('Fetching preview from:', currentDoc.preview_url);

        const response = await fetch(currentDoc.preview_url, {
          headers: {
            'ngrok-skip-browser-warning': 'true',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load: ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        console.log('Preview loaded successfully, blob URL:', objectUrl);

        // Cache the preview URL
        setDocumentPreviewCache(prev => ({
          ...prev,
          [currentDoc.uniqueKey]: objectUrl
        }));

        setInlinePreviewUrl(objectUrl);
      } catch (error) {
        console.error('Failed to load inline preview:', error);
        setInlinePreviewUrl(null);
      } finally {
        setInlinePreviewLoading(false);
      }
    };

    loadInlinePreview();
  }, [selectedDocId, documents, documentPreviewCache]);  // Reload preview when selected document or documents list changes
  useEffect(() => {
    if (selected?.id) {
      loadFNOLData(selected.id);
      loadDocuments(selected.id);
      loadOriginalEvidence(selected.id);
      // Validity data will be loaded only when navigating to Validation page
    }
  }, [selected?.id, loadDocuments, loadOriginalEvidence]);

  // Edit mode functions
  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (originalForm) {
      setForm(originalForm);
    }
    setIsEditMode(false);
    setHasChanges(false);
  };

  const handleSubmit = async () => {
    if (!selected?.id || !hasChanges) return;

    try {
      setLoadingFNOL(true);

      // Format form data for API
      const apiData = {
        loss_name: form.lossName,
        claimant: form.claimant || null,
        date_of_loss: form.dateOfLoss || null,
        time_of_loss: sanitizeTimeForApi(form.timeOfLoss),
        cause_of_loss: form.cause,
        loss_description: form.description || null,
        estimated_loss_currency: form.currency,
        estimated_loss_amount: parseFloat(form.amount) || 0,
        loss_location: form.location,
        gps_coordinates: form.gps || null,
        report_date: form.reportedDate ? `${form.reportedDate}T10:30:00` : null,
        reported_by: form.reportedBy || null,
        global_loss_details: form.globalLossEvent || null,
        policy_number: form.policyNumber || null,
        multiple_policy_number: null,
        sublimit: (() => {
          if (!form.subLimitType || Object.keys(form.subLimitType).length === 0) return null;
          return form.subLimitType;
        })()
      };

      const response = await fnolAPI.updateFNOL(selected.id, apiData);
      if (response.success) {
        // Optimistic Update: Update local state immediately with returned data
        const updatedFNOL = response.data;

        // Helper to map API data to form state (re-defined here or move to outer scope)
        const mapFNOLDataToForm = (fnolData, policyData = {}) => {
          return {
            lossName: fnolData.loss_name || "",
            claimant: fnolData.claimant || "",
            dateOfLoss: fnolData.date_of_loss ? fnolData.date_of_loss.split('T')[0] : "",
            timeOfLoss: normalizeApiTimeToInput(fnolData.time_of_loss),
            cause: fnolData.cause_of_loss || "",
            description: fnolData.loss_description || "",
            currency: fnolData.estimated_loss_currency || "",
            amount: fnolData.estimated_loss_amount?.toString() || "",
            location: fnolData.loss_location || "",
            gps: fnolData.gps_coordinates || "",
            reportedDate: fnolData.report_date ? fnolData.report_date.split('T')[0] : "",
            reportedBy: fnolData.reported_by || "",
            globalLossEvent: fnolData.global_loss_details || "",
            policyNumber: fnolData.policy_number || "",
            multiplePolicyNumber: fnolData.multiple_policy_number || null,
            // Policy details
            insuredName: policyData.insured_name || "",
            inceptionDate: policyData.inception_date ? policyData.inception_date.split('T')[0] : "",
            expirationDate: policyData.expiration_date ? policyData.expiration_date.split('T')[0] : "",
            policyStatus: policyData.policy_status || "",
            lineOfBusiness: policyData.line_of_business || "",
            broker: policyData.broker_name || "",
            subLimitType: fnolData.sublimit && typeof fnolData.sublimit === 'object'
              ? fnolData.sublimit
              : (typeof fnolData.sublimit === 'string'
                ? fnolData.sublimit.split(',').reduce((acc, s) => {
                  if (s.trim()) acc[s.trim()] = true;
                  return acc;
                }, {})
                : {}),
          };
        };

        // Fetch policy details if policy number exists (for complete form state)
        let policyData = {};
        if (updatedFNOL.policy_number) {
          try {
            // Check if we already have policy data in current form and it matches
            if (form.policyNumber === updatedFNOL.policy_number && form.insuredName) {
              policyData = {
                insured_name: form.insuredName,
                inception_date: form.inceptionDate,
                expiration_date: form.expirationDate,
                policy_status: form.policyStatus,
                line_of_business: form.lineOfBusiness,
                broker_name: form.broker
              };
            } else {
              const policyResponse = await fnolAPI.getPolicyDetails(updatedFNOL.policy_number);
              if (policyResponse.success) {
                policyData = policyResponse.data;
              }
            }
          } catch (e) { console.error("Error fetching policy for update", e); }
        }

        const newFormData = mapFNOLDataToForm(updatedFNOL, policyData);
        setForm(newFormData);
        setOriginalForm(newFormData);
        setHasChanges(false);
        setIsEditMode(false);

        // Update apiInbox locally
        setApiInbox(prev => prev.map(item => {
          if (item.id === selected.id) {
            return {
              ...item,
              company: policyData.insured_name || updatedFNOL.insured_name || updatedFNOL.claimant || "Unknown Insured",
              brief: updatedFNOL.loss_description || "",
              data: updatedFNOL
            };
          }
          return item;
        }));

        // Start re-validation process in modal
        setShowRevalidationModal(true);
        setRevalidating(true);

        try {
          // Delete existing validity
          await fnolAPI.deleteValidityCheck(selected.id);

          // Create new validity
          await fnolAPI.createValidityCheck(selected.id);

          // Fetch updated validity data
          await loadValidityData(selected.id);

          // Show success toast
          setSuccessToast({ message: 'Validation completed successfully!' });

        } catch (revalError) {
          console.error('Failed to re-validate:', revalError);
          setSuccessToast({ message: 'Validation update failed, but FNOL was saved.', type: 'warning' });
        } finally {
          setRevalidating(false);
          setShowRevalidationModal(false);
        }

        // NO re-fetch of loadFNOLsFromAPI() to avoid stale data override
        // await loadFNOLsFromAPI();
      }
    } catch (error) {
      console.error('Failed to update FNOL:', error);
      alert('Failed to update FNOL. Please try again.');
    } finally {
      setLoadingFNOL(false);
    }
  };

  return (
    <div className="desktop-root fnol-app bg-[#e7f3ff] text-dark" style={{
      fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Arial",
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflow: 'hidden'
    }}>
      {/* Notification Toast for new FNOLs */}
      <NotificationToast
        notification={toastNotification}
        onClose={() => setToastNotification(null)}
        duration={5000}
      />

      {/* Success Toast */}
      {successToast && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div className={`toast show ${successToast.type === 'warning' ? 'bg-warning' : 'bg-success'} text-white`} role="alert">
            <div className="toast-body d-flex align-items-center">
              <i className={`bi ${successToast.type === 'warning' ? 'bi-exclamation-triangle' : 'bi-check-circle'} me-2`}></i>
              {successToast.message}
              <button type="button" className="btn-close btn-close-white ms-auto" onClick={() => setSuccessToast(null)}></button>
            </div>
          </div>
        </div>
      )}

      {/* NAVBAR */}
      <NavBar />

      {/* MAIN */}
      <div className="container-fluid" style={{ flex: 1, overflow: 'hidden', paddingTop: '.8rem', paddingLeft: '.5rem', paddingRight: '.5rem', minHeight: 0 }}>
        <div className="row gx-1 h-100">
          {/* LEFT: Collapsible Inbox */}
          <FNOLInbox
            isSplitScreen={isSplitScreen}
            isOpen={inboxOpen}
            onToggle={() => {
              if (isSplitScreen) {
                setIsSplitScreen(false);
                setInboxOpen(true);
              } else {
                setInboxOpen(!inboxOpen);
              }
            }}
            inbox={currentInbox}
            filteredInbox={filteredInbox}
            selectedId={selectedId}
            unreadCount={unreadCount}
            query={query}
            startDate={startDate}
            endDate={endDate}
            startTime={startTime}
            endTime={endTime}
            onQueryChange={setQuery}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
            onResetFilters={resetFilters}
            onItemSelect={async (it) => {
              setSelectedId(it.id);
              // Navigate to datahandling page for selected FNOL
              navigate(`/${it.id}/datahandling`);
              if (apiInbox.length > 0) {
                if (!it.read) {
                  try {
                    await fnolAPI.updateFNOLStatus(it.id, 1);
                    setApiInbox((prev) => prev.map((x) => (x.id === it.id ? { ...x, read: true } : x)));
                  } catch (error) {
                    console.error('Failed to mark FNOL as read:', error);
                  }
                } else {
                  setApiInbox((prev) => prev.map((x) => (x.id === it.id ? { ...x, read: true } : x)));
                }
              } else {
                setInbox((prev) => prev.map((x) => (x.id === it.id ? { ...x, read: true } : x)));
              }
            }}
            onToggleRead={toggleRead}
          />

          {/* CENTER + RIGHT: main card with center content and always-on Document Viewer */}
          <main className="main-content-area col h-100" style={{ paddingLeft: '5px', overflow: 'hidden', transition: 'all 0.5s ease' }}>
            <div className="card h-100" style={{ backgroundColor: 'transparent', border: 'none', flexShrink: 0 }}>
              <div className="h-100 d-flex flex-column" style={{ paddingTop: '1px' }}>

                {selected ? (
                  <>
                    {/* PROGRESS STEPPER */}
                    {!isSplitScreen && (
                      <div className="flex-shrink-0">
                        <ProgressStepper
                          isCompleted={isCompleted}
                          isValidationCompleted={isValidationCompleted}
                          isFraudAnalysisCompleted={isFraudAnalysisCompleted}
                          isTriagingCompleted={isTriagingCompleted}
                          isClaimsSetUpCompleted={isClaimsSetUpCompleted}
                          currentStep={currentStep}
                          maxStepReached={maxReachedStep}
                          onStepClick={(step) => {
                            const stepIndex = STEP_ORDER.indexOf(step);
                            // Prevent navigation to future steps that haven't been reached yet
                            if (stepIndex > maxReachedStep) return;

                            // Data Handling is always accessible
                            if (step === 'DataHandling') {
                              goToDataHandling();
                              return;
                            }

                            // For other steps, check if required fields are filled
                            if (!validateRequiredFields(form)) {
                              console.warn(`⚠️ Cannot navigate to ${step}: Required FNOL fields are missing`);
                              alert('Please complete all required fields in Data Handling before proceeding.');
                              return;
                            }

                            // Navigate to the selected step
                            if (step === 'Validation') goToValidation();
                            if (step === 'FraudAnalysis') goToFraudAnalysis();
                            if (step === 'Triaging') goToTriaging();
                            if (step === 'ClaimsSetup') goToClaimsSetup();
                          }}
                        />

                        {/* Center content (form) */}
                        <CompanyCard
                          selected={selected}
                          onClaimGenerated={handleClaimGenerated}
                          externalClaimId={claimId}
                        />
                      </div>
                    )}

                    <div className="flex-grow-1 " style={{ minHeight: 0 }}>
                      {currentStep === 'DataHandling' ? (
                        <DataHandlingPage
                          isSplitScreen={isSplitScreen}
                          inboxOpen={inboxOpen}
                          form={form}
                          setF={setF}
                          isEditMode={isEditMode}
                          loadingFNOL={loadingFNOL}
                          isCompleted={isCompleted}
                          handleEdit={handleEdit}
                          handleComplete={handleComplete}
                          handleCancel={handleCancel}
                          handleSubmit={handleSubmit}
                          hasChanges={hasChanges}
                          revalidating={revalidating}
                          documents={documents}
                          filteredDocuments={filteredDocuments}
                          loadingDocuments={loadingDocuments}
                          selectedDocId={selectedDocId}
                          setSelectedDocId={setSelectedDocId}
                          selectedDoc={selectedDoc}
                          documentSearchQuery={documentSearchQuery}
                          setDocumentSearchQuery={setDocumentSearchQuery}
                          openPreview={openPreview}
                          inlinePreviewUrl={inlinePreviewUrl}
                          inlinePreviewLoading={inlinePreviewLoading}
                          setIsSplitScreen={setIsSplitScreen}
                          setInboxOpen={setInboxOpen}
                          onContinue={goToValidation}
                          hasClaimId={!!claimId}
                          originalEvidence={originalEvidence}
                          fnolId={selected?.id}
                        />
                      ) : currentStep === 'Validation' && validateRequiredFields(form) ? (
                        <ValidationPage
                          selected={selected}
                          validityData={validityData}
                          loadingValidity={loadingValidity}
                          isCompleted={isValidationCompleted}
                          setIsCompleted={handleSetValidationCompleted}
                          onPrevious={goToDataHandling}
                          onNext={goToFraudAnalysis}
                          hasClaimId={!!claimId}
                        />
                      ) : currentStep === 'FraudAnalysis' && validateRequiredFields(form) ? (
                        <FraudAnalysisPage
                          selected={selected}
                          analysisData={analysisData}
                          loadingAnalysis={loadingAnalysis}
                          isCompleted={isFraudAnalysisCompleted}
                          setIsCompleted={handleSetFraudAnalysisCompleted}
                          onPrevious={goToValidation}
                          onNext={goToTriaging}
                          hasClaimId={!!claimId}
                        />
                      ) : currentStep === 'Triaging' && validateRequiredFields(form) ? (
                        <TriagingPage
                          selected={selected}
                          isCompleted={isTriagingCompleted}
                          setIsCompleted={handleSetTriagingCompleted}
                          onPrevious={goToFraudAnalysis}
                          onNext={goToClaimsSetup}
                          hasClaimId={!!claimId}
                        />
                      ) : currentStep === 'ClaimsSetup' && validateRequiredFields(form) ? (
                        <ClaimsSetUpPage
                          selected={selected}
                          isCompleted={isClaimsSetUpCompleted}
                          setIsCompleted={handleSetClaimsSetUpCompleted}
                          onPrevious={goToTriaging}
                          goToDataHandling={goToDataHandling}
                          goToValidation={goToValidation}
                          goToTriaging={goToTriaging}
                          hasClaimId={!!claimId}
                          onClaimGenerated={handleClaimGenerated}
                          allPriorStepsCompleted={isCompleted && isValidationCompleted && isFraudAnalysisCompleted && isTriagingCompleted}
                        />
                      ) : null}
                    </div>
                  </>
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                    <div style={{ opacity: 0.2 }}>
                      <img src="/AT_FullLogo.png" alt="" style={{ width: '22.25rem', height: '6.25rem', paddingRight: '0.625rem' }} />
                    </div>
                    <div className="h5 text-secondary" style={{ opacity: 0.4 }}>Select an FNOL from the inbox to view details</div>
                  </div>
                )}
              </div>
            </div>
          </main>

        </div>
      </div>
      {/* Copyright Footer */}
      <div className="w-100 d-flex align-items-center justify-content-center" style={{ height: '1.5rem', backgroundColor: '#D3E8FF', color: '#006BB3', fontSize: '0.7rem', fontWeight: '500', flexShrink: 0 }}>
        &copy; {new Date().getFullYear()}. Alpha Torana Private Limited All rights reserved.
      </div>

      {/* PREVIEW MODAL */}
      {isPreviewOpen && previewDoc && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1055 }} role="dialog" aria-modal="true">
          <div className="position-absolute top-0 start-0 w-100 h-100" onClick={closePreview} aria-hidden />
          <div className="position-relative bg-white rounded shadow" style={{ margin: '1.5rem', width: 'calc(100% - 3rem)', maxWidth: '80%', height: '80vh', overflow: 'hidden' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center border">📄</div>
                <div>
                  <div className="font-semibold">{previewDoc.name}</div>
                  <div className="text-xs text-slate-500">{previewDoc.type}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={previewDoc.download_url.replace('http://', 'https://')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                >
                  Download
                </a>
                <button onClick={closePreview} className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-200" aria-label="Close preview"><CloseIcon /></button>
              </div>
            </div>

            <div className="w-full h-full bg-gray-50 flex items-center justify-center">
              {previewLoading ? (
                <div className="text-center">
                  <div className="spinner-border text-primary mb-2" role="status">
                    <span className="visually-hidden">Loading preview...</span>
                  </div>
                  <div className="text-slate-600">Loading document preview...</div>
                </div>
              ) : previewError ? (
                <div className="p-6 text-center text-slate-600">
                  <div className="text-2xl mb-2">⚠️ Preview Error</div>
                  <div className="text-sm">{previewError}</div>
                  <div className="text-sm mt-2">Try downloading the file instead.</div>
                </div>
              ) : previewContent ? (
                (() => {
                  const fileExtension = previewDoc.name.split('.').pop().toLowerCase();
                  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension);

                  if (isImage) {
                    return (
                      <img
                        src={previewContent}
                        alt={previewDoc.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    );
                  } else if (fileExtension === 'pdf') {
                    return (
                      <iframe
                        src={previewContent}
                        title={previewDoc.name}
                        className="w-full h-full border-0"
                      />
                    );
                  } else {
                    return (
                      <div className="p-6 text-center text-slate-600">
                        <div className="text-2xl mb-2">📄</div>
                        <div className="text-sm">Document loaded successfully</div>
                        <div className="text-sm">Use download button to view the file</div>
                      </div>
                    );
                  }
                })()
              ) : (
                <div className="p-6 text-center text-slate-600">
                  <div className="text-2xl mb-2">Preview not available</div>
                  <div className="text-sm">Unable to load document preview</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Re-validation Modal */}
      {showRevalidationModal && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: 1060 }} role="dialog" aria-modal="true">
          <div className="position-absolute top-0 start-0 w-100 h-100" aria-hidden />
          <div className="position-relative bg-white rounded shadow p-4" style={{ maxWidth: '400px', width: '90%' }}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Re-validating...</span>
              </div>
              <h5 className="mb-3">Re-validating FNOL</h5>
              <p className="text-muted mb-0">Please wait while we update the validation for the changes made...</p>
            </div>
          </div>
        </div>
      )}

      {/* FNOL Submission Form Overlay */}
      {showSubmissionForm && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center" style={{ zIndex: 1050 }}>
          <div className="container" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <FNOLSubmissionForm
              onSuccess={handleFNOLSubmission}
              onCancel={() => setShowSubmissionForm(false)}
            />
          </div>
        </div>
      )}

      {/* API Error Alert */}
      {apiError && (
        <div className="position-fixed bottom-0 end-0 m-3" style={{ zIndex: 1060 }}>
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {apiError}
            <button type="button" className="btn-close" onClick={() => setApiError(null)}></button>
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="position-fixed top-50 start-50 translate-middle" style={{ zIndex: 1060 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
}
